import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import LanguageStore from '../store/LanguageStore';
import VideoService from '../services/VideoService';
import JsonLd from '../components/JsonLd/JsonLd';
import { createVideoSchema, createBreadcrumbSchema } from '../utils/jsonLdSchemas';
import './video-detail-page.scss';

const VideoDetailPage = observer(() => {
    const { videoId } = useParams();
    const navigate = useNavigate();
    const t = LanguageStore.t;
    const { language } = LanguageStore;

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [videoData, setVideoData] = useState(null);

    useEffect(() => {
        const fetchVideoDetail = async () => {
            try {
                setLoading(true);
                const data = await VideoService.getVideoDetail(videoId);
                if (data.success) {
                    setVideoData(data);
                } else {
                    setError(data.error || 'Failed to load video details');
                }
            } catch (err) {
                console.error('Error fetching video detail:', err);
                setError(err.response?.data?.error || 'Failed to load video details');
            } finally {
                setLoading(false);
            }
        };

        if (videoId) {
            fetchVideoDetail();
        }
    }, [videoId]);

    // Устанавливаем meta title страницы
    useEffect(() => {
        if (videoData && videoData.video) {
            const title = language === 'ru' 
                ? `Видео статистика по видеоролику ${videoData.video.title}`
                : `Video statistics for ${videoData.video.title}`;
            document.title = title;
        }

        // Очистка при размонтировании компонента
        return () => {
            document.title = language === 'ru' ? 'Видео статистика' : 'Video Statistics';
        };
    }, [videoData, language]);

    if (loading) {
        return (
            <div className="video-detail-page">
                <div className="video-detail-page__loading">
                    <div className="video-detail-page__spinner"></div>
                    <p>{t('loading') || 'Loading...'}</p>
                </div>
            </div>
        );
    }

    if (error || !videoData) {
        return (
            <div className="video-detail-page">
                <div className="video-detail-page__error">
                    <p>{error || 'Video not found'}</p>
                    <button onClick={() => navigate('/')}>{t('close') || 'Close'}</button>
                </div>
            </div>
        );
    }

    const { video, statistics } = videoData;
    const chartData = statistics.chartData;

    // Создаем JSON-LD схемы
    const siteUrl = window.location.origin;
    const videoSchema = createVideoSchema({ ...video, statistics }, siteUrl, language);
    const breadcrumbSchema = createBreadcrumbSchema([
        { name: language === 'ru' ? 'Главная' : 'Home', url: '/' },
        { name: language === 'ru' ? 'Видео' : 'Videos', url: '/videos' },
        { name: video.title, url: `/video/${video.videoId}` }
    ], siteUrl);

    return (
        <div className="video-detail-page">
            <JsonLd data={videoSchema} id="json-ld-video" />
            <JsonLd data={breadcrumbSchema} id="json-ld-breadcrumb" />
            <article className="container">
                <div className="video-detail-page__header">
                    <button 
                        className="video-detail-page__back"
                        onClick={() => navigate(-1)}
                    >
                        ← {language === 'ru' ? 'Назад' : 'Back'}
                    </button>
                    <h1 className="video-detail-page__title">{video.title}</h1>
                </div>

                <div className="video-detail-page__content">
                    <div className="video-detail-page__top-section">
                        {video.imageWebUrl && (
                            <div className="video-detail-page__preview">
                                <a 
                                    href={`https://www.youtube.com/watch?v=${video.videoId}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="video-detail-page__preview-link"
                                >
                                    <img
                                        src={video.imageWebUrl}
                                        alt={video.title}
                                        width={video.imageWidth}
                                        height={video.imageHeight}
                                        className="video-detail-page__preview-image"
                                    />
                                </a>
                            </div>
                        )}
                        <div className="video-detail-page__info">
                        <div className="video-detail-page__info-item">
                            <span className="video-detail-page__info-label">
                                {language === 'ru' ? 'Канал' : 'Channel'}:
                            </span>
                            <span className="video-detail-page__info-value">
                                <a 
                                    href={`https://www.youtube.com/channel/${video.channel.channelId}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="video-detail-page__channel-link"
                                >
                                    {video.channel.title}
                                </a>
                            </span>
                        </div>
                        <div className="video-detail-page__info-item">
                            <span className="video-detail-page__info-label">
                                {language === 'ru' ? 'Первое появление в трендах' : 'First trending date'}:
                            </span>
                            <span className="video-detail-page__info-value">
                                {statistics.firstTrendingDate}
                            </span>
                        </div>
                        <div className="video-detail-page__info-item">
                            <span className="video-detail-page__info-label">
                                {language === 'ru' ? 'Всего дней в трендах' : 'Total trending days'}:
                            </span>
                            <span className="video-detail-page__info-value">
                                {statistics.totalTrendingDays}
                            </span>
                        </div>
                        <div className="video-detail-page__info-item">
                            <span className="video-detail-page__info-label">
                                {language === 'ru' ? 'Всего стран' : 'Total countries'}:
                            </span>
                            <span className="video-detail-page__info-value">
                                {statistics.totalUniqueCountries}
                            </span>
                        </div>
                        </div>
                    </div>

                    <div className="video-detail-page__stats">
                        <h2 className="video-detail-page__stats-title">
                            {language === 'ru' ? 'Последние статистические данные' : 'Latest statistics'}
                        </h2>
                        <div className="video-detail-page__stats-grid">
                            <div className="video-detail-page__stat-card">
                                <div className="video-detail-page__stat-icon">👁️</div>
                                <div className="video-detail-page__stat-label">{t('views') || 'Views'}</div>
                                <div className="video-detail-page__stat-value">
                                    {statistics.currentStats.viewCount?.toLocaleString() || 0}
                                </div>
                                <div className="video-detail-page__stat-date">
                                    {statistics.currentStats.trackedAt}
                                </div>
                            </div>
                            <div className="video-detail-page__stat-card">
                                <div className="video-detail-page__stat-icon">👍</div>
                                <div className="video-detail-page__stat-label">{t('likes') || 'Likes'}</div>
                                <div className="video-detail-page__stat-value">
                                    {statistics.currentStats.likeCount?.toLocaleString() || 0}
                                </div>
                                <div className="video-detail-page__stat-date">
                                    {statistics.currentStats.trackedAt}
                                </div>
                            </div>
                            <div className="video-detail-page__stat-card">
                                <div className="video-detail-page__stat-icon">💬</div>
                                <div className="video-detail-page__stat-label">{t('comments') || 'Comments'}</div>
                                <div className="video-detail-page__stat-value">
                                    {statistics.currentStats.commentCount?.toLocaleString() || 0}
                                </div>
                                <div className="video-detail-page__stat-date">
                                    {statistics.currentStats.trackedAt}
                                </div>
                            </div>
                        </div>
                    </div>

                    {chartData && (
                        <div className="video-detail-page__chart">
                            <h2 className="video-detail-page__chart-title">
                                {language === 'ru' ? 'График прироста показателей' : 'Metrics Growth Chart'}
                            </h2>
                            <div className="video-detail-page__chart-table">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>{language === 'ru' ? 'Дата' : 'Date'}</th>
                                            <th>{t('views') || 'Views'}</th>
                                            <th>{t('likes') || 'Likes'}</th>
                                            <th>{t('comments') || 'Comments'}</th>
                                            <th>{language === 'ru' ? 'Стран' : 'Countries'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {chartData.labels.map((label, index) => {
                                            const viewsGrowth = chartData.viewsGrowth?.[index];
                                            const likesGrowth = chartData.likesGrowth?.[index];
                                            const commentsGrowth = chartData.commentsGrowth?.[index];
                                            
                                            const formatValueWithGrowth = (value, growth) => {
                                                const formattedValue = value?.toLocaleString() || 0;
                                                if (growth !== null && growth !== undefined) {
                                                    const growthClass = growth >= 0 ? 'video-detail-page__growth--positive' : 'video-detail-page__growth--negative';
                                                    const growthText = `${growth >= 0 ? '+' : ''}${growth}%`;
                                                    return (
                                                        <>
                                                            {formattedValue}
                                                            {' '}
                                                            <span className={growthClass}>({growthText})</span>
                                                        </>
                                                    );
                                                }
                                                return formattedValue;
                                            };
                                            
                                            return (
                                                <tr key={index}>
                                                    <td>{label}</td>
                                                    <td>{formatValueWithGrowth(chartData.views[index], viewsGrowth)}</td>
                                                    <td>{formatValueWithGrowth(chartData.likes[index], likesGrowth)}</td>
                                                    <td>{formatValueWithGrowth(chartData.comments[index], commentsGrowth)}</td>
                                                    <td>{chartData.countriesCount?.[index] ?? 0}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {!chartData && statistics.totalTrendingDays <= 3 && (
                        <div className="video-detail-page__no-chart">
                            <p>
                                {language === 'ru' 
                                    ? 'График доступен только для видео, которые были в трендах более 3 дней'
                                    : 'Chart is available only for videos that were trending for more than 3 days'
                                }
                            </p>
                        </div>
                    )}
                </div>
            </article>
        </div>
    );
});

export default VideoDetailPage;

